// src/components/Topbar.jsx
import { NavLink, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import i18n from "../i18n";
import { useState, useRef, useEffect } from "react";

export default function Topbar({ user, onLogout, hideNav = false }) {
  const { t } = useTranslation();

  const link = ({ isActive }) =>
    `px-3 py-1.5 rounded-lg text-sm transition-colors ${
      isActive
        ? "bg-white/20 text-white font-medium"
        : "text-blue-100 hover:text-white hover:bg-white/10"
    }`;

  const isManager = user?.derived?.isManager;
  const isOrgAdmin =
    user?.derived?.isOrgAdmin ||
    (Array.isArray(user?.roles) && user.roles.includes("org_super_admin"));
  const isPlatformAdmin =
    user?.derived?.isPlatformAdmin ||
    (Array.isArray(user?.roles) && user.roles.includes("platform_admin"));

  const changeLanguage = (lng) => {
    i18n.changeLanguage(lng);
  };

  return (
    <div className="sticky top-0 z-20 bg-gradient-to-r from-blue-800 via-blue-700 to-indigo-700 border-b border-white/10 shadow-md">
      <div className="max-w-6xl mx-auto px-4 py-2.5 flex items-center gap-3">
        {/* Brand area */}
        <NavLink
          to="/dashboard"
          className="flex items-center gap-2.5 flex-shrink-0 focus:outline-none focus:ring-2 focus:ring-white/30 rounded-xl"
        >
          <span className="flex items-center justify-center bg-white/95 rounded-xl p-1 shadow-sm">
            <img
              src="/favicon.png"
              alt="ZeroRiscuri"
              className="h-7 w-7 object-contain"
            />
          </span>
          <span className="flex flex-col leading-tight">
            <span className="font-bold text-white tracking-tight text-sm">ZeroRiscuri</span>
            <span className="hidden sm:block text-blue-200 text-[10px] leading-tight">Work Management</span>
          </span>
        </NavLink>

        <div className="h-5 w-px bg-white/20 ml-1" />

        {!hideNav && (
          <nav className="flex items-center gap-0.5">
            {!isPlatformAdmin && (
              <NavLink to="/dashboard" className={link}>
                {t("home")}
              </NavLink>
            )}
            {!isPlatformAdmin && (
              <NavLink to="/certificates" className={link}>
                {t ? t("certificates.tab_mine") : "Certificates"}
              </NavLink>
            )}
            {!isPlatformAdmin && (
              <NavLink to="/trainings" className={link}>
                {t("myTrainings")}
              </NavLink>
            )}
            {isManager && (
              <NavLink to="/team" className={link}>
                {t("team.title")}
              </NavLink>
            )}
            {isOrgAdmin && (
              <>
                <NavLink to="/admin/users" className={link}>
                  {t("users")}
                </NavLink>
                <NavLink to="/admin/worksite-docs" className={link}>
                  {t("worksiteDocumentation")}
                </NavLink>
              </>
            )}
            {isPlatformAdmin && (
              <>
                <NavLink to="/platform/dashboard" className={link}>
                  {t("platform_dashboard.title")}
                </NavLink>
                <NavLink to="/admin/assignments" className={link}>
                  {t("assignAssignments")}
                </NavLink> {/* This was moved from OrgAdmin, not 100% sure it is working properly, to check with backend */}
                <NavLink to="/platform/trainings" className={link}>
                  {t("trainingsAdmin")}
                </NavLink>
                <NavLink to="/platform/route-declarations" className={link}>
                  {t("routeDeclarations", "Declarații traseu")}
                </NavLink>
              </>
            )}
          </nav>
        )}

        <div className="ml-auto flex items-center gap-2">
          <EmailMenu user={user} onLogout={onLogout} />
          {/* Language switcher */}
          <div className="flex gap-0.5 bg-white/10 border border-white/20 rounded-lg p-0.5">
            <button
              onClick={() => changeLanguage("en")}
              className={`px-2.5 py-1 rounded-md text-xs font-medium transition-colors ${
                i18n.language === "en"
                  ? "bg-white text-blue-700 shadow-sm"
                  : "text-blue-100 hover:text-white hover:bg-white/10"
              }`}
            >
              EN
            </button>
            <button
              onClick={() => changeLanguage("ro")}
              className={`px-2.5 py-1 rounded-md text-xs font-medium transition-colors ${
                i18n.language === "ro"
                  ? "bg-white text-blue-700 shadow-sm"
                  : "text-blue-100 hover:text-white hover:bg-white/10"
              }`}
            >
              RO
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function EmailMenu({ user, onLogout }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  const nav = useNavigate();
  const { t } = useTranslation();

  useEffect(() => {
    function onDoc(e) {
      if (!ref.current) return;
      if (!ref.current.contains(e.target)) setOpen(false);
    }
    document.addEventListener("click", onDoc);
    return () => document.removeEventListener("click", onDoc);
  }, []);

  return (
    <div className="relative" ref={ref}>
      <button
        className="hidden sm:inline-flex items-center gap-2 text-sm text-blue-100 hover:text-white px-3 py-1.5 rounded-lg hover:bg-white/10 transition-colors border border-white/20 focus:outline-none focus:ring-2 focus:ring-white/30"
        onClick={() => setOpen((s) => !s)}
        aria-expanded={open}
      >
        <span className="w-6 h-6 rounded-full bg-white text-blue-700 text-xs font-semibold flex items-center justify-center flex-shrink-0">
          {(user?.name || user?.email || "?")[0].toUpperCase()}
        </span>
        <span className="max-w-[160px] truncate">{user?.name || user?.email}</span>
        <svg className="w-3 h-3 text-blue-200" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </button>
      {open && (
        <div className="absolute right-0 mt-1.5 w-48 bg-white border border-slate-200 rounded-xl shadow-lg py-1 z-50">
          <div className="px-3 py-2 border-b border-slate-100">
            <p className="text-xs font-medium text-slate-900 truncate">{user?.name || "—"}</p>
            <p className="text-xs text-slate-500 truncate">{user?.email}</p>
          </div>
          <button
            className="block w-full text-left px-3 py-2 text-sm text-slate-700 hover:bg-slate-50 transition-colors"
            onClick={() => {
              setOpen(false);
              nav("/profile");
            }}
          >
            {t("profile.title", "Profile")}
          </button>
          <button
            className="block w-full text-left px-3 py-2 text-sm text-red-600 hover:bg-red-50 transition-colors"
            onClick={() => {
              setOpen(false);
              onLogout?.();
            }}
          >
            {t("logout")}
          </button>
        </div>
      )}
    </div>
  );
}

